import { useTranslation } from 'react-i18next';
import '../../locales/studentNetworkV2/studentNetworkV2-en.json';

// Sketch: capsule tags — #ecf0f3 bg, 15px corners, 12px semibold #282828 text, px-2.5 py-1.5
function CapsuleTag({ text }: { text: string }) {
    return (
        <span className="inline-block rounded-full bg-slate-grey-100 px-2.5 py-1.5 text-xxs font-semibold text-[#282828]">
            {text}
        </span>
    );
}

// Sketch: section heading — emoji + bold 18px heading
function SectionHeading({ emoji, title }: { emoji: string; title: string }) {
    return (
        <div className="mb-3 flex items-center gap-2">
            <span className="w-5 text-center text-base">{emoji}</span>
            <span className="text-lg font-semibold text-black">{title}</span>
        </div>
    );
}

// Sketch: 3-line experience item — bold title (16px weight 6), detail (16px), date (16px slate-grey)
function ExperienceItem({ title, detail, date }: { title: string; detail?: string; date?: string }) {
    return (
        <div className="mb-4">
            <div className="text-base font-semibold text-black">{title}</div>
            {detail && <div className="text-base text-black">{detail}</div>}
            {date && <div className="text-base text-slate-grey">{date}</div>}
        </div>
    );
}

type ProfileSectionsProps = {
    bio: string;
    interests: string[];
    lookingFor: string[];
    educationLines: { institution: string; detail: string; date?: string }[];
    workLines: { organization: string; role: string }[];
};

export function ProfileSections({ bio, interests, lookingFor, educationLines, workLines }: ProfileSectionsProps) {
    const { t } = useTranslation('back_royal', { keyPrefix: 'studentNetworkV2.studentNetworkV2.profileDetail' });

    return (
        <>
            {/* Bio */}
            {bio && (
                <div className="mb-6">
                    <span className="text-base font-semibold italic text-black">&ldquo;{bio}&rdquo;</span>
                </div>
            )}

            {/* Sections */}
            <div className="flex flex-col gap-6">
                {interests.length > 0 && (
                    <div>
                        <SectionHeading emoji="🔥" title={t('interests')} />
                        <div className="flex flex-wrap gap-1.5">
                            {interests.map(interest => (
                                <CapsuleTag key={interest} text={interest} />
                            ))}
                        </div>
                    </div>
                )}

                {lookingFor.length > 0 && (
                    <div>
                        <SectionHeading emoji="👀" title={t('lookingFor')} />
                        <div className="flex flex-wrap gap-1.5">
                            {lookingFor.map(item => (
                                <CapsuleTag key={item} text={item} />
                            ))}
                        </div>
                    </div>
                )}

                {educationLines.length > 0 && (
                    <div>
                        <SectionHeading emoji="🍎" title={t('education')} />
                        {educationLines.map(line => (
                            <ExperienceItem
                                key={`${line.institution}-${line.detail}`}
                                title={line.institution}
                                detail={line.detail}
                                date={line.date}
                            />
                        ))}
                    </div>
                )}

                {workLines.length > 0 && (
                    <div>
                        <SectionHeading emoji="🚀" title={t('workHistory')} />
                        {workLines.map(line => (
                            <ExperienceItem
                                key={`${line.organization}-${line.role}`}
                                title={line.organization}
                                detail={line.role}
                            />
                        ))}
                    </div>
                )}
            </div>
        </>
    );
}
